import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import PageHero from "../components/sections/PageHero";
import { api, formatApiErrorDetail } from "../lib/api";

const matterTypes = [
  "Civil Litigation",
  "Criminal Litigation",
  "Commercial Dispute",
  "Real Estate / RERA",
  "Corporate Advisory",
  "Contract Drafting",
  "Legal Notice",
  "Other",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    matter: "",
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.matter) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (!form.consent) {
      toast.error("Please acknowledge the consent statement to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        matter_type: form.matter,
        message: form.message,
        page_source: typeof window !== "undefined" ? window.location.pathname : "",
        consent: form.consent,
      });
      toast.success("Thank you. The Lex Polaris team will be in touch shortly.");
      setForm({ name: "", phone: "", email: "", matter: "", message: "", consent: false });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Get in Touch"
        title="Contact Lex Polaris"
        subtitle="For consultation, legal advisory or representation-related enquiries, connect with Lex Polaris, Advocates & Solicitors."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
      />

      <section className="bg-ivory section-y" data-testid="contact-section">
        <div className="container-px max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 lg:gap-16">
          {/* DETAILS */}
          <div className="md:col-span-5">
            <div className="overline mb-6">Reach Us</div>
            <h2 className="h-display text-3xl md:text-4xl text-navy mb-10">
              Direct lines, office &amp; consultation hours.
            </h2>

            <ul className="space-y-7">
              <li className="flex items-start gap-5" data-testid="contact-detail-phone">
                <span className="mt-1 w-10 h-10 flex items-center justify-center border border-navy/20 text-navy">
                  <Phone size={16} />
                </span>
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-charcoal/55 mb-1.5">Phone</div>
                  <a href="tel:+919810899124" className="font-serif text-2xl text-navy hover:text-gold transition-colors block">
                    +91 98108 99124
                  </a>
                  <a href="tel:+911244231444" className="text-charcoal/70 hover:text-navy transition-colors text-sm">
                    Landline: 0124-4231444
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-5" data-testid="contact-detail-whatsapp">
                <span className="mt-1 w-10 h-10 flex items-center justify-center border border-navy/20 text-navy">
                  <MessageCircle size={16} />
                </span>
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-charcoal/55 mb-1.5">WhatsApp</div>
                  <a
                    href="https://wa.me/919810899124?text=Hello%20Lex%20Polaris%20Team%2C%20I%20would%20like%20to%20discuss%20a%20legal%20matter."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-serif text-2xl text-navy hover:text-gold transition-colors"
                  >
                    +91 98108 99124
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-5" data-testid="contact-detail-email">
                <span className="mt-1 w-10 h-10 flex items-center justify-center border border-navy/20 text-navy">
                  <Mail size={16} />
                </span>
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-charcoal/55 mb-1.5">Email</div>
                  <a href="mailto:admin@lexpolaris.in" className="font-serif text-2xl text-navy hover:text-gold transition-colors break-all">
                    admin@lexpolaris.in
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-5" data-testid="contact-detail-address">
                <span className="mt-1 w-10 h-10 flex items-center justify-center border border-navy/20 text-navy">
                  <MapPin size={16} />
                </span>
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-charcoal/55 mb-1.5">Office</div>
                  <p className="font-serif text-xl text-navy leading-snug mb-2">
                    Lex Polaris, Advocates &amp; Solicitors
                  </p>
                  <p className="text-charcoal/70 text-sm leading-relaxed">
                    G Block, 22nd Floor, M3M Broadway,<br />
                    SPR Road, Sector 71,<br />
                    Gurugram, Haryana 122101
                  </p>
                  <a
                    href="https://maps.google.com/?q=M3M+Broadway+Sector+71+Gurugram"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link mt-4 inline-flex"
                    data-testid="contact-map-link"
                  >
                    View on Map
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-5" data-testid="contact-detail-hours">
                <span className="mt-1 w-10 h-10 flex items-center justify-center border border-navy/20 text-navy">
                  <Clock size={16} />
                </span>
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-charcoal/55 mb-1.5">Hours</div>
                  <p className="font-serif text-xl text-navy">10:00 AM &ndash; 7:00 PM</p>
                  <p className="text-charcoal/65 text-sm">Monday to Saturday</p>
                </div>
              </li>
            </ul>
          </div>

          {/* FORM */}
          <div className="md:col-span-7">
            <div className="bg-white border border-soft-grey p-8 md:p-12" data-testid="contact-form-wrapper">
              <div className="overline mb-5">Consultation Enquiry</div>
              <h3 className="h-display text-3xl md:text-4xl text-navy mb-3">Tell us about your matter.</h3>
              <p className="text-charcoal/70 text-sm mb-10">
                Submission of this form does not create a lawyer-client relationship.
              </p>

              <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Full Name" required>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      data-testid="contact-input-name"
                      className="w-full bg-transparent border-b border-soft-grey focus:border-navy py-3 text-charcoal placeholder:text-charcoal/40 outline-none transition-colors"
                      placeholder="Your full name"
                      required
                    />
                  </Field>
                  <Field label="Phone Number" required>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      data-testid="contact-input-phone"
                      className="w-full bg-transparent border-b border-soft-grey focus:border-navy py-3 text-charcoal placeholder:text-charcoal/40 outline-none transition-colors"
                      placeholder="+91 ..."
                      required
                    />
                  </Field>
                </div>
                <Field label="Email Address" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-input-email"
                    className="w-full bg-transparent border-b border-soft-grey focus:border-navy py-3 text-charcoal placeholder:text-charcoal/40 outline-none transition-colors"
                    placeholder="you@email.com"
                    required
                  />
                </Field>
                <Field label="Matter Type" required>
                  <select
                    value={form.matter}
                    onChange={update("matter")}
                    data-testid="contact-input-matter"
                    className="w-full bg-transparent border-b border-soft-grey focus:border-navy py-3 text-charcoal outline-none transition-colors appearance-none cursor-pointer"
                    required
                  >
                    <option value="">Select a matter type</option>
                    {matterTypes.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Message">
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-input-message"
                    className="w-full bg-transparent border-b border-soft-grey focus:border-navy py-3 text-charcoal placeholder:text-charcoal/40 outline-none transition-colors resize-none"
                    placeholder="Briefly describe your matter (without sharing confidential details)."
                  />
                </Field>

                <label className="flex items-start gap-3 pt-2 cursor-pointer" data-testid="contact-consent-label">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={update("consent")}
                    data-testid="contact-input-consent"
                    className="mt-1 accent-navy w-4 h-4 flex-shrink-0"
                  />
                  <span className="text-xs text-charcoal/70 leading-relaxed">
                    I understand that submitting this form does not create a lawyer-client relationship and
                    that the information submitted will be reviewed for consultation purposes only.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                >
                  {submitting ? "Sending..." : "Send Enquiry"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.22em] uppercase text-charcoal/60 mb-2">
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
